import { FunctionComponent, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import TrashIcon from '@heroicons/react/24/solid/TrashIcon';
import EyeIcon from '@heroicons/react/24/solid/EyeIcon';
import ArrowDownTrayIcon from '@heroicons/react/24/solid/ArrowDownTrayIcon';
import PlusIcon from '@heroicons/react/24/solid/PlusIcon';
import { fetchAuthWrapper } from '@starjack/api';
import { useNotification } from '@starjack/features/notifications';
import { useGetLiftOperatorProductsQuery } from '@starjack/entities/liftoperator';
import { StarjackTable } from '@/components/Table/v2/Table';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useGetB2BUsersQuery } from '@/api/v2/b2b';
import {
    pdfBackgroundApi,
    pdfBackgroundDesignTemplateUrl,
    pdfBackgroundUrl,
    useGetPdfBackgroundsQuery,
} from '@/api/v2/pdfBackground';
import {
    PDF_BACKGROUND_SCOPE_LABELS,
    PDF_BACKGROUND_TYPE_LABELS,
    PdfBackground,
    PdfBackgroundType,
    TICKET_PDF_BACKGROUND_TYPES,
} from '@/models/pdfBackground';
import { PdfBackgroundUploadDialog } from './PdfBackgroundUploadDialog';
import { PdfBackgroundPreviewDialog } from './PdfBackgroundPreviewDialog';
import { PdfBackgroundDeleteDialog } from './PdfBackgroundDeleteDialog';
import { useDispatch } from 'react-redux';

type TypeFilter = PdfBackgroundType | 'ALL';

type EnrichedBackground = PdfBackground & {
    userLabel: string;
    productLabel: string;
};

export const PdfBackgroundsContent: FunctionComponent = () => {
    const { addNotification } = useNotification();
    const dispatch = useDispatch();
    const { liftOperatorData } = useAuthentication();
    const liftOperatorId = liftOperatorData?.liftOperatorId;

    const { data: backgroundsResponse, isLoading: backgroundsLoading } =
        useGetPdfBackgroundsQuery(undefined, { skip: !liftOperatorId });
    const { data: b2bData } = useGetB2BUsersQuery(undefined, {
        skip: !liftOperatorId,
    });
    const { data: products } = useGetLiftOperatorProductsQuery(
        { liftOperatorId: liftOperatorId ?? '' },
        { skip: !liftOperatorId }
    );

    const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [previewTarget, setPreviewTarget] = useState<PdfBackground | null>(
        null
    );
    const [deleteTarget, setDeleteTarget] = useState<PdfBackground | null>(
        null
    );

    const userNameById = useMemo(() => {
        const map = new Map<string, string>();
        b2bData?.b2bUsers?.forEach((u) => map.set(u.id, u.name));
        return map;
    }, [b2bData]);

    const productNameByShortId = useMemo(() => {
        const map = new Map<string, string>();
        products?.forEach((p) => {
            if (p.shortProductId && p.name?.de) {
                map.set(p.shortProductId, p.name.de);
            }
        });
        return map;
    }, [products]);

    const enrichedRows = useMemo<EnrichedBackground[]>(() => {
        const all = backgroundsResponse?.backgrounds ?? [];
        return all
            .filter((bg) => bg.type !== PdfBackgroundType.INVOICE)
            .filter((bg) =>
                typeFilter === 'ALL' ? true : bg.type === typeFilter
            )
            .map((bg) => ({
                ...bg,
                userLabel: bg.userId
                    ? (userNameById.get(bg.userId) ?? bg.userId)
                    : '—',
                productLabel: bg.shortProductId
                    ? (productNameByShortId.get(bg.shortProductId) ??
                      bg.shortProductId)
                    : '—',
            }))
            .sort((a, b) => {
                if (a.type !== b.type) return a.type.localeCompare(b.type);
                if (a.scope !== b.scope) return a.scope.localeCompare(b.scope);
                return a.storagePath.localeCompare(b.storagePath);
            });
    }, [backgroundsResponse, typeFilter, userNameById, productNameByShortId]);

    const handleDownloadDesignTemplate = async (type: PdfBackgroundType) => {
        try {
            const response = await fetchAuthWrapper(
                pdfBackgroundDesignTemplateUrl(type),
                { headers: { Accept: 'application/pdf' } }
            );
            if (!response.ok) {
                throw new Error('Designvorlage konnte nicht geladen werden.');
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `designvorlage-${type}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            addNotification({
                message: 'Designvorlage konnte nicht heruntergeladen werden.',
                type: 'error',
            });
        }
    };

    const handleDelete = async (background: PdfBackground) => {
        try {
            const response = await fetchAuthWrapper(
                pdfBackgroundUrl(background.type, {
                    userId: background.userId,
                    shortProductId: background.shortProductId,
                }),
                { method: 'DELETE' }
            );
            if (!response.ok) {
                throw new Error('Löschen fehlgeschlagen.');
            }
            addNotification({
                message: 'Vorlage gelöscht.',
                type: 'success',
            });
            dispatch(pdfBackgroundApi.util.invalidateTags(['PDF_BACKGROUND']));
            setDeleteTarget(null);
        } catch {
            addNotification({
                message: 'Vorlage konnte nicht gelöscht werden.',
                type: 'error',
            });
        }
    };

    return (
        <Box>
            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='flex-start'
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant='h4'>Ticketvorlagen</Typography>
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 1, maxWidth: 720 }}
                    >
                        Verwalten Sie alle Hintergrund-PDFs für Smartphone-,
                        Pick-Up- und Barcode-Tickets. Optional können Vorlagen
                        einem B2B-User und/oder einem Produkt zugeordnet werden.
                        Die spezifischste Vorlage gewinnt beim Druck.
                    </Typography>
                </Box>
                <Button
                    variant='contained'
                    startIcon={
                        <Box
                            component='span'
                            sx={{ display: 'flex', width: 20 }}
                        >
                            <PlusIcon />
                        </Box>
                    }
                    onClick={() => setUploadDialogOpen(true)}
                >
                    Neue Vorlage hochladen
                </Button>
            </Stack>

            <Box sx={{ mb: 4 }}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Designvorlagen herunterladen
                </Typography>
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 2, maxWidth: 720 }}
                >
                    Diese statischen PDFs zeigen die fix eingedruckten Bereiche
                    pro Tickettyp. Geben Sie sie an Ihre Gestaltung weiter,
                    damit das Design die Platzhalter nicht überdeckt.
                </Typography>
                <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
                    {TICKET_PDF_BACKGROUND_TYPES.map((type) => (
                        <Button
                            key={type}
                            variant='outlined'
                            startIcon={
                                <Box
                                    component='span'
                                    sx={{ display: 'flex', width: 20 }}
                                >
                                    <ArrowDownTrayIcon />
                                </Box>
                            }
                            onClick={() => handleDownloadDesignTemplate(type)}
                        >
                            {PDF_BACKGROUND_TYPE_LABELS[type]}
                        </Button>
                    ))}
                </Stack>
            </Box>

            <Stack direction='row' spacing={2} sx={{ mb: 3 }}>
                <TextField
                    select
                    label='Typ filtern'
                    size='small'
                    value={typeFilter}
                    onChange={(e) =>
                        setTypeFilter(e.target.value as TypeFilter)
                    }
                    sx={{ minWidth: 220 }}
                >
                    <MenuItem value='ALL'>Alle Typen</MenuItem>
                    {TICKET_PDF_BACKGROUND_TYPES.map((value) => (
                        <MenuItem key={value} value={value}>
                            {PDF_BACKGROUND_TYPE_LABELS[value]}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>

            <StarjackTable<EnrichedBackground>
                items={enrichedRows}
                count={enrichedRows.length}
                loading={backgroundsLoading}
                onPageChange={() => {
                    /* client-side, no-op */
                }}
                rowsPerPage={enrichedRows.length || 25}
                noDataLabel='Keine Vorlagen hinterlegt. Klicken Sie auf „Neue Vorlage hochladen", um zu starten.'
                columns={[
                    {
                        label: 'Tickettyp',
                        itemTransform: (row) => (
                            <Typography variant='body2'>
                                {PDF_BACKGROUND_TYPE_LABELS[row.type]}
                            </Typography>
                        ),
                    },
                    {
                        label: 'Gilt für',
                        itemTransform: (row) => (
                            <Chip
                                size='small'
                                label={PDF_BACKGROUND_SCOPE_LABELS[row.scope]}
                                variant='outlined'
                            />
                        ),
                    },
                    {
                        label: 'B2B-User',
                        itemTransform: (row) => (
                            <Typography variant='body2'>
                                {row.userLabel}
                            </Typography>
                        ),
                    },
                    {
                        label: 'Produkt',
                        itemTransform: (row) => (
                            <Typography variant='body2'>
                                {row.productLabel}
                            </Typography>
                        ),
                    },
                    {
                        label: 'Aktionen',
                        options: {
                            headerCellProps: { align: 'right' },
                            rowCellProps: { align: 'right' },
                        },
                        itemTransform: (row) => (
                            <Stack
                                direction='row'
                                spacing={1}
                                justifyContent='flex-end'
                            >
                                <Tooltip title='Vorschau anzeigen'>
                                    <IconButton
                                        size='small'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPreviewTarget(row);
                                        }}
                                    >
                                        <Box
                                            sx={{ width: 20, display: 'flex' }}
                                        >
                                            <EyeIcon />
                                        </Box>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title='Vorlage löschen'>
                                    <IconButton
                                        size='small'
                                        color='error'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteTarget(row);
                                        }}
                                    >
                                        <Box
                                            sx={{ width: 20, display: 'flex' }}
                                        >
                                            <TrashIcon />
                                        </Box>
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        ),
                    },
                ]}
            />

            <PdfBackgroundUploadDialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                b2bUsers={b2bData?.b2bUsers ?? []}
                products={products ?? []}
                existingBackgrounds={backgroundsResponse?.backgrounds ?? []}
                onUploaded={() => {
                    dispatch(
                        pdfBackgroundApi.util.invalidateTags(['PDF_BACKGROUND'])
                    );
                }}
            />

            <PdfBackgroundPreviewDialog
                background={previewTarget}
                onClose={() => setPreviewTarget(null)}
            />

            <PdfBackgroundDeleteDialog
                background={deleteTarget}
                onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
            />
        </Box>
    );
};
